"use client";

import { Activity, ArrowLeft, ChartBar as BarChart3, Compass, Network, RefreshCw, Users } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Specializations() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const services = [{
    icon: Activity,
    title: "Fire Hazard Analysis",
    description: "Rigorous diagnostic assessments of industrial processes to identify ignition sources and mitigate operational risk.",
    href: "/ServicesPage"
  }, {
    icon: Network,
    title: "System Integration",
    description: "Engineering cohesive fire protection architectures that align with complex industrial infrastructure requirements.",
    href: "/ServicesPage"
  }, {
    icon: RefreshCw,
    title: "Risk Mitigation",
    description: "Systematic reduction of liability through advanced engineering controls and proactive safety management protocols.",
    href: "/ServicesPage"
  }, {
    icon: Compass,
    title: "Strategic Compliance",
    description: "Navigating complex regulatory landscapes with precision-engineered safety roadmaps for large-scale facilities.",
    href: "/ServicesPage"
  }, {
    icon: Users,
    title: "Technical Advisory",
    description: "Expert consultation for insurance underwriters and stakeholders requiring deep-dive technical risk validation.",
    href: "/ServicesPage"
  }, {
    icon: BarChart3,
    title: "Performance Metrics",
    description: "Quantifiable safety data modeling that bridges the gap between operational performance and insurance underwriting.",
    href: "/ServicesPage"
  }];
  return <section data-section-id="21817" id="core-specializations" className="bg-background text-foreground py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={intro.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20 md:mb-24 transition-all duration-700 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7 lg:order-1 order-1">
            <div className="h-px w-10 mb-6" style={{
            backgroundColor: "hsl(240 100% 25%)"
          }} aria-hidden="true" />
            <h2 className="font-default font-semibold text-foreground leading-[1.1] tracking-tight" style={{
            fontSize: "clamp(2rem, 3.6vw, 3.25rem)"
          }}>
              Engineering{" "}
              <span className="italic" style={{
              color: "hsl(240 100% 25%)"
            }}>
                Resilience
              </span>{" "}
              at Scale
            </h2>
          </div>

          <div className="lg:col-span-5 lg:order-2 order-2 flex flex-col justify-end">
            <p className="font-default text-muted-foreground leading-[1.7]" style={{
            fontSize: "clamp(1rem, 1.15vw, 1.0625rem)"
          }}>
              Bedrock Safety Engineering delivers deliberate, technical fire protection and risk management solutions for high-stakes industrial, aerospace, and pharmaceutical environments.
            </p>
            <Link to="/ServicesPage" className="group inline-flex items-center gap-2 mt-6 font-default text-sm font-medium w-fit" style={{
            color: "hsl(240 100% 25%)"
          }}>
              <span className="relative">
                View Technical Services
                <span className="absolute right-0 -bottom-0.5 h-px w-full origin-right scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100" style={{
                backgroundColor: "hsl(240 100% 25%)"
              }} aria-hidden="true" />
              </span>
              <ArrowLeft className="size-4 transition-transform duration-300 ease-out group-hover:-translate-x-1" strokeWidth={1.25} />
            </Link>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <ServiceCard key={i} data-index={i} icon={Icon} title={service.title} description={service.description} href={service.href} index={i} />;
        })}
        </div>
      </div>
    </section>;
}
function ServiceCard({
  icon: Icon,
  title,
  description,
  href,
  index,
  ...rest
}: {
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  href: string;
  index: number;
  [key: string]: any;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`group relative bg-card border border-border p-8 md:p-10 transition-all duration-700 ease-out hover:border-[hsl(240_100%_25%)] flex flex-col shadow-md ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    borderRadius: "2px",
    transitionDelay: isVisible ? `${index * 80}ms` : "0ms"
  }}>
      <div className="text-foreground transition-colors duration-300 ease-out group-hover:text-[hsl(240_100%_25%)]">
        <Icon className="size-7" strokeWidth={1.25} />
      </div>

      <div className="mt-8">
        <h3 className="font-default text-xl font-semibold text-foreground leading-snug tracking-tight">
          {title}
        </h3>
        <p className="mt-4 font-default text-[15px] leading-[1.7] text-muted-foreground">
          {description}
        </p>
      </div>

      <div className="mt-8 pt-2">
        <Link to={href} className="inline-flex items-center gap-2 font-default text-sm font-medium" style={{
        color: "hsl(240 100% 25%)"
      }}>
          <span className="relative">
            Learn More
            <span className="absolute right-0 -bottom-0.5 h-px w-full origin-right scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100" style={{
            backgroundColor: "hsl(240 100% 25%)"
          }} aria-hidden="true" />
          </span>
          <ArrowLeft className="size-4 transition-transform duration-300 ease-out group-hover:-translate-x-1" strokeWidth={1.25} />
        </Link>
      </div>
    </div>;
}